import { Injectable, inject } from "@angular/core";
import { SignUpEntryService } from "./entry.service";
import { SignUpCreateAccountService } from "./create-account.service";
import { IdentityService } from "../../../../common/services/identity.service";
import { filter, take } from "rxjs/operators";
import { SignUpAvatarService } from "./avatar.service";
import { SignUpUserCustomizationService } from "./user-customization.service";
import { SignUpHubspotService } from "./hubspot.service";
import { ApiSessionService } from "../../../../common/services/api/api-session.service";
import { UserService } from "../../../../common/services/api/resources/user/user.service";
import { lastValueFrom } from "rxjs";

const debug = true;

@Injectable({ providedIn: "root" })
export class SignUpShotCallerService {
  private identityService = inject(IdentityService);
  private apiSession = inject(ApiSessionService);
  private entry = inject(SignUpEntryService);
  private account = inject(SignUpCreateAccountService);
  private avatar = inject(SignUpAvatarService);
  private userCustomization = inject(SignUpUserCustomizationService);
  private hubspot = inject(SignUpHubspotService);
  private userService = inject(UserService);

  // account information after signup
  private _created!: string;
  private _accountId!: number;
  private _name!: string;
  private _email!: string;
  private _username!: string;
  private _domain!: string;
  private _userId!: number;
  private _organizationId!: number;
  private _orgName!: string;

  signupAndLogin(): Promise<void> {
    return (
      new Promise((resolve, reject) => {
        if (!this.entry.cookie) {
          this.account
            .createFreemiumAccount(
              this.entry.email,
              this.entry.verificationToken,
              this.entry.orgName,
              this.entry.contactPhone,
              this.entry.accountPlan
            )
            .then(res => {
              debug && console.warn("Signup result:", res);
              this._accountId = parseInt(res.accountId);
              this._created = res.created;
              this._email = res.contact.email;
              if (res.username) this._username = res.username;
              if (res.domain) this._domain = res.domain;
              if (this._username && this._domain) {
                const loginUsername = this._username + "@" + this._domain;
                const pword = this.account.password + "";
                this.apiSession
                  .login(loginUsername, pword)
                  .then(resolve)
                  .catch((err: Error) => {
                    console.error("Signup successful, but login attempt failed.", err);
                    reject(err);
                  });
              } else {
                // TODO: error handling for bad login
                console.error("WHOOPS - Bad login after account creation.");
                reject();
              }
            })
            .catch(reject);
        } else if (this.entry.cookie) {
          this.account
            .createOauthAccount(
              this.entry.email,
              this.entry.oAuthService,
              this.entry.oAuthServiceUserId,
              this.entry.oauthToken,
              this.entry.orgName,
              this.entry.contactPhone,
              this.entry.accountPlan
            )
            .then(account => {
              debug && console.warn("Signup result: ", account);
              this._accountId = parseInt(account.accountId);
              this._created = account.account.created;
              this._email = account.account.contact.email;
              this._userId = parseInt(account.userOAuth2AccessToken.userId);
            })
            .then(() =>
              this.apiSession.oAuthPostSignupLogin(
                this.entry.oauthToken || "",
                this.entry.oAuthServiceUserId || "",
                this.entry.oAuthService || "",
                this._userId + ""
              )
            )
            .then(resolve)
            .catch(reject);
        } else {
          reject();
        }
      })
        .then(() => lastValueFrom(this.userService.selfUser.pipe(take(1))))
        // we will just convert the userService observable to a promise for now.
        // we can probably make this whole promise chain into an observable once the other third tier services are done
        .then(apiUser => {
          debug && console.warn("User after signup:", apiUser);
          this._userId = parseInt(apiUser.userId);
          this._organizationId = parseInt(apiUser.organizationId);
          this._orgName = apiUser.contact.organization;
          this._name = apiUser.contact.name;
          if (!this.domain) this._domain = apiUser.domain;

          this.identityService.state
            .pipe(
              filter(state => state.addresses.length > 0),
              take(1)
            )
            .subscribe(state => {
              const firstAddress = state.addresses[0];
              this.identityService.setDefaultIdentity(firstAddress.aor);

              this.hubspot.postAccountCreateHubspotRequest({
                email: this._email,
                sip_address: firstAddress.aor,
                team_create_account_id: this._accountId.toString(),
                team_create_account_created: this._created,
                team_create_username: firstAddress.username,
                team_create_subdomain: firstAddress.domain,
                company: this._orgName,
                jobtitle: this.userCustomization.title
              });
            });
        })
        .then(() => this.avatar.saveAvatar())
        .then(() => this.userCustomization.saveTitle())
        .then(() => {}) // return void
        .catch(err => console.error("Signup error", err))
    );
  }

  get created(): string {
    return this._created;
  }
  get accountId(): number {
    return this._accountId;
  }
  get name(): string {
    return this._name;
  }
  get email(): string {
    return this._email;
  }
  get username(): string {
    return this._username;
  }
  get domain(): string {
    return this._domain;
  }
  get userId(): number {
    return this._userId;
  }
  get organizationId(): number {
    return this._organizationId;
  }
  get orgName(): string {
    return this._orgName;
  }
}
